<template>
  <div class="delivery-list-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <h2>发货单管理</h2>
        <p>管理所有发货单据，支持查看、编辑和导出</p>
      </div>
      <div class="header-actions">
        <el-button @click="manageTemplates">
          <i class="el-icon-setting"></i>
          模板管理
        </el-button>
        <el-button type="primary" @click="createDelivery">
          <i class="el-icon-plus"></i>
          新建发货单
        </el-button>
      </div>
    </div>

    <!-- 搜索区域 -->
    <div class="search-area">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="订单号">
          <el-input 
            v-model="searchForm.orderNo" 
            placeholder="请输入订单号"
            clearable
            style="width: 200px;"
          ></el-input>
        </el-form-item>
        <el-form-item label="客户名称">
          <el-input 
            v-model="searchForm.customerName" 
            placeholder="请输入客户名称"
            clearable
            style="width: 200px;"
          ></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 发货单列表 -->
    <div class="table-container">
      <el-table 
        :data="deliveryList" 
        border 
        style="width: 100%"
        v-loading="loading"
      >
        <el-table-column prop="orderNo" label="订单号" width="150"></el-table-column>
        <el-table-column prop="customerName" label="客户名称" width="120"></el-table-column>
        <el-table-column prop="deliveryDate" label="配送日期" width="120"></el-table-column>
        <el-table-column prop="address" label="配送地址" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="contact" label="联系人" width="100"></el-table-column>
        <el-table-column prop="phone" label="联系电话" width="120"></el-table-column>
        <el-table-column prop="deliveryPerson" label="配送员" width="100"></el-table-column>
        <el-table-column prop="totalAmount" label="总金额" width="100" align="right">
          <template slot-scope="scope">
            ¥{{ scope.row.totalAmount }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="150">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="viewDelivery(scope.row)">查看</el-button>
            <el-button type="text" size="small" @click="editDelivery(scope.row)">编辑</el-button>
            <el-button type="text" size="small" @click="exportDelivery(scope.row)">导出</el-button>
            <el-button type="text" size="small" @click="deleteDelivery(scope.row)" style="color: #f56c6c;">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        ></el-pagination>
      </div>
    </div>

    <!-- 查看详情对话框 -->
    <el-dialog 
      title="发货单详情" 
      :visible.sync="detailVisible" 
      width="80%" 
      top="5vh"
    >
      <div class="detail-content" v-if="currentDelivery">
        <div class="detail-info">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="订单号">{{ currentDelivery.orderNo }}</el-descriptions-item>
            <el-descriptions-item label="客户名称">{{ currentDelivery.customerName }}</el-descriptions-item>
            <el-descriptions-item label="配送日期">{{ currentDelivery.deliveryDate }}</el-descriptions-item>
            <el-descriptions-item label="联系人">{{ currentDelivery.contact }}</el-descriptions-item>
            <el-descriptions-item label="联系电话">{{ currentDelivery.phone }}</el-descriptions-item>
            <el-descriptions-item label="配送员">{{ currentDelivery.deliveryPerson }}</el-descriptions-item>
            <el-descriptions-item label="配送地址" :span="2">{{ currentDelivery.address }}</el-descriptions-item>
            <el-descriptions-item label="备注" :span="2">{{ currentDelivery.remark || '无' }}</el-descriptions-item>
          </el-descriptions>
        </div>
        
        <div class="goods-info">
          <h4>商品明细</h4>
          <el-table :data="currentDelivery.goodsList" border>
            <el-table-column prop="name" label="商品名称"></el-table-column>
            <el-table-column prop="spec" label="规格"></el-table-column>
            <el-table-column prop="unit" label="单位"></el-table-column>
            <el-table-column prop="quantity" label="数量"></el-table-column>
            <el-table-column prop="price" label="单价"></el-table-column>
            <el-table-column prop="amount" label="金额"></el-table-column>
            <el-table-column prop="remark" label="备注"></el-table-column>
          </el-table>
          <div class="total-summary">
            <strong>总金额: ¥{{ currentDelivery.totalAmount }}</strong>
          </div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="primary" @click="exportDelivery(currentDelivery)">导出PDF</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'DeliveryList',
  data() {
    return {
      searchForm: {
        orderNo: '',
        customerName: ''
      },
      deliveryList: [],
      loading: false,
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      detailVisible: false,
      currentDelivery: null
    }
  },
  
  created() {
    this.loadDeliveryList()
    this.initSampleData()
  },
  
  methods: {
    // 初始化示例数据
    initSampleData() {
      const sampleData = [
        {
          id: '1',
          orderNo: 'SO20240115001',
          customerName: '张三商店',
          deliveryDate: '2024-01-15',
          address: '江西省南昌市红谷滩新区凤凰中大道',
          contact: '张三',
          phone: '13800138000',
          deliveryPerson: '李四',
          totalAmount: '299.50',
          status: 'pending',
          remark: '请注意保鲜',
          createdAt: '2024-01-15 09:30:00',
          goodsList: [
            { name: '苹果', spec: '红富士', unit: '斤', quantity: 10, price: 8.50, amount: 85.00, remark: '新鲜' },
            { name: '香蕉', spec: '进口', unit: '斤', quantity: 5, price: 6.80, amount: 34.00, remark: '' }
          ]
        },
        {
          id: '2',
          orderNo: 'SO20240115002',
          customerName: '李四超市',
          deliveryDate: '2024-01-16',
          address: '江西省南昌市东湖区八一大道',
          contact: '李四',
          phone: '13900139000',
          deliveryPerson: '王五',
          totalAmount: '456.80',
          status: 'completed',
          remark: '',
          createdAt: '2024-01-15 10:15:00',
          goodsList: [
            { name: '橙子', spec: '脐橙', unit: '斤', quantity: 8, price: 12.50, amount: 100.00, remark: '甜' },
            { name: '葡萄', spec: '巨峰', unit: '斤', quantity: 6, price: 25.80, amount: 154.80, remark: '无籽' }
          ]
        }
      ]
      
      this.deliveryList = sampleData
      this.pagination.total = sampleData.length
    },
    
    // 加载发货单列表
    loadDeliveryList() {
      this.loading = true
      // 这里可以调用API获取数据
      setTimeout(() => {
        this.loading = false
      }, 500)
    },
    
    // 搜索
    handleSearch() {
      this.loadDeliveryList()
    },
    
    // 重置搜索
    resetSearch() {
      this.searchForm = {
        orderNo: '',
        customerName: ''
      }
      this.loadDeliveryList()
    },
    
    // 创建发货单
    createDelivery() {
      this.$router.push('/delivery/create')
    },
    
    // 管理模板
    manageTemplates() {
      this.$router.push('/delivery/template-list')
    },
    
    // 查看发货单
    viewDelivery(row) {
      this.currentDelivery = row
      this.detailVisible = true
    },
    
    // 编辑发货单
    editDelivery(row) {
      this.$router.push({
        path: '/delivery/create',
        query: { id: row.id }
      })
    },
    
    // 导出发货单
    exportDelivery(row) {
      this.$message.info('PDF导出功能开发中...')
    },
    
    // 删除发货单
    deleteDelivery(row) {
      this.$confirm('确定要删除这个发货单吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$message.success('删除成功')
        this.loadDeliveryList()
      }).catch(() => {
        // 用户取消删除
      })
    },
    
    // 获取状态类型
    getStatusType(status) {
      const statusMap = {
        pending: 'warning',
        processing: 'primary',
        completed: 'success',
        cancelled: 'danger'
      }
      return statusMap[status] || 'info'
    },
    
    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        pending: '待配送',
        processing: '配送中',
        completed: '已完成',
        cancelled: '已取消'
      }
      return statusMap[status] || '未知'
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.loadDeliveryList()
    },
    
    // 当前页变化
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.loadDeliveryList()
    }
  }
}
</script>

<style scoped>
.delivery-list-container {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.header-left h2 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 24px;
}

.header-left p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.search-area {
  margin-bottom: 20px;
  padding: 16px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.search-form {
  margin: 0;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.detail-content {
  max-height: 70vh;
  overflow: auto;
}

.detail-info {
  margin-bottom: 20px;
}

.goods-info h4 {
  margin: 20px 0 10px 0;
  color: #333;
}

.total-summary {
  margin-top: 16px;
  text-align: right;
  font-size: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 16px;
  }
  
  .header-actions {
    align-self: stretch;
  }
  
  .search-form {
    flex-direction: column;
  }
}
</style>